import React, { useContext, useMemo, useState, useEffect } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { gql, useQuery, useMutation } from "@apollo/client";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import EntityOperationBar from "../../components/EntityOperationBar";
import JobSchedulesCalendarView from "./JobSchedulesCalendarView";
import JobSchedulesListingView from "./JobSchedulesListingView";
import JobScheduleSidebar from "./JobScheduleSidebar";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";

const UI_STRINGS = {
    PAGE_TITLE: "Job Schedules",
    ENTITY_SINGULAR: "Job Schedule",
    ENTITY_PLURAL: "Job Schedules",
    DELETE_TITLE_SINGLE: (name) => `Delete Job Schedule ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Job Schedules?`,
    DELETE_MESSAGE_SINGLE: (
        name,
    ) => `You are about to delete job schedule "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} job schedule${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total job schedules selected: ${count}`,
};

const JobSchedules = (props) => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeJobId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite   = can("job_schedule", "WRITE");
    const canDestroy = can("job_schedule", "DESTROY");
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState("calendar");
    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "calendar" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [filterText, setFilterText] = useState("");
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [routeEditJob, setRouteEditJob] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_JOB_SCHEDULES = gql`
        query GetJobSchedules($plantId: Int!) {
            jobSchedules(where: { plant_id: { equals: $plantId } }) {
                id
                name
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
                description
                cycle {
                    created_at
                    description
                    id
                    is_active
                    name
                    plant_id
                    plant {
                        code
                        name
                    }
                }
                cycle_id
                part_id
                part {
                    id
                    name
                    plant_id
                    description
                    is_active
                }
                device_id
                end_time
                start_time
                plant_id
                plant {
                    name
                    id
                }
            }
        }
    `;

    const CREATE_ONE_JOB_SCHEDULE = gql`
        mutation CreateOneJobSchedule($data: JobScheduleCreateInput!) {
            createOneJobSchedule(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_JOB_SCHEDULE = gql`
        mutation UpdateOneJobSchedule(
            $data: JobScheduleUpdateInput!
            $where: JobScheduleWhereUniqueInput!
        ) {
            updateOneJobSchedule(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    const DELETE_ONE_JOB_SCHEDULE = gql`
        mutation DeleteOneJobSchedule($id: Int!) {
            deleteOneJobSchedule(where: { id: $id }) {
                id
            }
        }
    `;

    const { data, loading, error, refetch } = useQuery(GET_JOB_SCHEDULES, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [createJobSchedule] = useMutation(CREATE_ONE_JOB_SCHEDULE, {
        client: starekContext.engineClient,
    });
    const [updateJobSchedule] = useMutation(UPDATE_ONE_JOB_SCHEDULE, {
        client: starekContext.engineClient,
    });
    const [deleteOneJobSchedule] = useMutation(DELETE_ONE_JOB_SCHEDULE, {
        client: starekContext.engineClient,
    });

    const jobSchedules = data?.jobSchedules ?? [];

    useEffect(() => {
        if (!routeId) {
            setRouteEditJob(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/job-schedules", searchParams);

        if (routeJobId == null) {
            starekContext.showError("Job schedule not found");
            setRouteEditJob(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const jobToEditRecord = jobSchedules.find(
            (job) => Number(job?.id) === routeJobId,
        );

        if (!jobToEditRecord) {
            starekContext.showError("Job schedule not found");
            setRouteEditJob(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditJob(jobToEditRecord);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeJobId
                ? current
                : [jobToEditRecord],
        );
        setShowSidebar(true);
    }, [
        routeId,
        routeJobId,
        loading,
        jobSchedules,
        searchParams,
        navigate,
        starekContext,
    ]);

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/job-schedules/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/job-schedules/${selectedRows[0].id}`, searchParams));
    };

    const handleEditJob = (job) => {
        navigate(buildListRoute(`/job-schedules/${job.id}`, searchParams));
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setRouteEditJob(null);
        if (routeId) {
            navigate(buildListRoute("/job-schedules", searchParams), {
                replace: true,
            });
        }
        await refetch();
        setToggleCleared(!toggleCleared);
        setSelectedRows([]);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveJobSchedule = async (jobPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            if (!jobPayload.id) {
                // Create
                await createJobSchedule({
                    variables: {
                        data: {
                            name: jobPayload.name,
                            description: jobPayload.description || "",
                            start_time: jobPayload.start_time,
                            end_time: jobPayload.end_time,
                            plant: {
                                connect: { id: starekContext.lastUsedPlant.id },
                            },
                            part: {
                                connect: { id: Number(jobPayload.part_id) },
                            },
                            cycle: {
                                connect: { id: Number(jobPayload.cycle_id) },
                            },
                            device: {
                                connect: { id: jobPayload.device_id },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            } else {
                // Update
                await updateJobSchedule({
                    variables: {
                        where: { id: jobPayload.id },
                        data: {
                            name: { set: jobPayload.name },
                            description: { set: jobPayload.description || "" },
                            start_time: { set: jobPayload.start_time },
                            end_time: { set: jobPayload.end_time },
                            part: {
                                connect: { id: Number(jobPayload.part_id) },
                            },
                            cycle: {
                                connect: { id: Number(jobPayload.cycle_id) },
                            },
                            device: {
                                connect: { id: jobPayload.device_id },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            await handleSidebarClose();
            starekContext.showSuccess(
                jobPayload.id
                    ? `Job Schedule "${jobPayload.name}" updated successfully`
                    : `Job Schedule "${jobPayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDeleteJobSchedule = async (job) => {
        try {
            await deleteOneJobSchedule({
                variables: { id: job.id },
            });
            await handleSidebarClose();
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDelete = (jobs) => {
        if (!jobs || jobs.length === 0 || isDeleting) return;

        const isSingle = jobs.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(jobs[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(jobs.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(jobs[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(jobs.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(jobs.length)}
                        &nbsp;
                        <span className="badge bg-info">{jobs.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                try {
                    for (const job of jobs) {
                        try {
                            await deleteOneJobSchedule({
                                variables: { id: job.id },
                            });
                        } catch (err) {
                            starekContext.showError(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    await refetch();
                    setSelectedRows([]);
                    setToggleCleared(!toggleCleared);
                } finally {
                    setIsDeleting(false);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    if (loading) return <EntityLoadingState icon="fa-calendar" />;

    return error ? (
        <div className="tw-text-center tw-mt-10 tw-text-red-600">
            Failed to load job schedules: {error.message}
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSearchParams({ view: mode });
                    setSelectedRows([]);
                }}
                onEdit={handleEdit}
                onDelete={() => handleDelete(selectedRows)}
            >
                <EntityOperationBar.View
                    mode="calendar"
                    icon="fa fa-calendar"
                    tooltip="Calendar View"
                >
                    <JobSchedulesCalendarView
                        jobSchedules={jobSchedules}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        onRefresh={refetch}
                        onEditJob={handleEditJob}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    <JobSchedulesListingView
                        items={jobSchedules}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={(state) =>
                            setSelectedRows(state.selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                        noDataComponent={<EntityEmptyState icon="fa-calendar" entityName="Job Schedule" messages={EMPTY_MESSAGES.jobSchedules} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <JobScheduleSidebar
                show={showSidebar}
                onDiscard={handleSidebarClose}
                item={routeEditJob || selectedRows[0] || null}
                plantId={starekContext.lastUsedPlant.id}
                onSave={handleSaveJobSchedule}
                onDelete={handleDeleteJobSchedule}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { JobSchedules };
